import React from "react";
import {
  BillingDetailsAndConfigurationResponse,
  CreateClientBillingDetailsParams,
} from "../../../../api/services/client/models";
import {
  MuiBox,
  MuiDoubleLabel,
  MuiGrid,
  MuiTypography,
  MuiViewMorePopOver,
} from "../../../../components";
import MuiChip, {
  ChipOptionsDataType,
} from "../../../../components/chip/MuiChip";
import {
  EMPTY_VALUE_DASH,
  viewMorePopOverGridSize,
} from "../../../../helpers/constants";
import { getValueOrDash, isNotEmpty } from "../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { billingAndPurchaseOrderKeys } from "../../../createClient/siteCore/SiteCoreKeys";
import { BillingDetailsWrapper } from "./ViewBillingDetails.style";

interface ViewBillingDetailsProps {
  data: BillingDetailsAndConfigurationResponse;
}

const ViewBillingDetails: React.FC<ViewBillingDetailsProps> = ({ data }) => {
  const { fetchLabel, commonLabels } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const { billingDetailsAndPOTitle } = billingAndPurchaseOrderKeys;

  const billingDetailsSiteCoreConfig: Partial<
    Record<
      keyof CreateClientBillingDetailsParams,
      keyof typeof billingAndPurchaseOrderKeys
    >
  > = {
    hasConsolidatedInvoicing: "consolidatedInvoicing",
    isActiveCompany: "activeCompany",
    isRebateClient: "rebateClient",
    hasEarlyPayDiscount: "earlyPayDiscount",
  };
  const getLabel = (key: keyof typeof billingAndPurchaseOrderKeys) => {
    return fetchLabel(
      billingDetailsAndPOTitle,
      billingAndPurchaseOrderKeys[key],
    );
  };

  const getChipValues = (billingDetails: CreateClientBillingDetailsParams) => {
    let chipValues: ChipOptionsDataType[] = [];

    const chipKeys = Object.keys(
      billingDetailsSiteCoreConfig,
    ) as (keyof CreateClientBillingDetailsParams)[];

    if (billingDetails) {
      chipKeys.forEach((key) => {
        if (billingDetails[key]) {
          chipValues.push({
            label: getLabel(
              billingDetailsSiteCoreConfig[
                key
              ] as keyof typeof billingAndPurchaseOrderKeys,
            ),
            value: key,
          });
        }
      });
    }

    return chipValues;
  };

  return (
    <BillingDetailsWrapper>
      <MuiGrid className="view-billing-details">
        <MuiTypography variant="h3">
          {getLabel("selectApplicable")}
        </MuiTypography>
        <MuiBox>
          {isNotEmpty(getChipValues(data.billingDetails)) ? (
            <MuiChip
              handleDelete={() => {}}
              isExpanded={true}
              isEditable={false}
              data={getChipValues(data.billingDetails)}
              showMoreARIALabel={commonARIALabel.showNMore}
              removeChipNameARIALabel={commonARIALabel.removeChipName}
            />
          ) : (
            EMPTY_VALUE_DASH
          )}
        </MuiBox>
        <MuiGrid container spacing={2} className="billing-details-grid">
          <MuiDoubleLabel
            label={getLabel("invoiceLanguage")}
            value={getValueOrDash(data?.billingDetails?.invoiceLanguage?.name)}
          />
          <MuiDoubleLabel
            label={getLabel("earlyPayDays")}
            value={
              data.billingDetails &&
              isNotEmpty(data.billingDetails.earlyPayDays)
                ? data.billingDetails.earlyPayDays
                : EMPTY_VALUE_DASH
            }
          />
          <MuiDoubleLabel
            label={getLabel("earlyPayPercentage")}
            value={
              data.billingDetails &&
              isNotEmpty(data.billingDetails.earlyPayPercentage)
                ? data.billingDetails.earlyPayPercentage
                : EMPTY_VALUE_DASH
            }
          />
        </MuiGrid>
        <MuiGrid size={viewMorePopOverGridSize}>
          <MuiDoubleLabel
            label={commonLabels.notes}
            value={
              data.billingDetails && isNotEmpty(data.billingDetails.notes) ? (
                <MuiViewMorePopOver
                  anchorText={commonLabels.viewMore}
                  message={data.billingDetails.notes}
                  placement="bottom-end"
                />
              ) : (
                EMPTY_VALUE_DASH
              )
            }
          ></MuiDoubleLabel>
        </MuiGrid>
      </MuiGrid>
    </BillingDetailsWrapper>
  );
};

export default ViewBillingDetails;
